import React, { useState, type JSX } from 'react';

import { authClient } from '../../lib/authClient';

interface Props {
  heading?: string;
  subheading?: string;
  /** Called after a successful sign-in or sign-up. */
  onSuccess?: () => void;
}

/**
 * Compact email+password auth form for players, with a toggle between sign-in
 * and sign-up. On success the Better Auth session updates and the surrounding
 * ConvexBetterAuthProvider flips Convex to authenticated — callers react to
 * that (useConvexAuth) rather than to onSuccess directly, but onSuccess is
 * offered for convenience.
 */
const AuthForm: React.FC<Props> = ({
  heading = 'Sign in to Magical Trails',
  subheading,
  onSuccess,
}): JSX.Element => {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result =
        mode === 'signin'
          ? await authClient.signIn.email({ email, password })
          : await authClient.signUp.email({
              email,
              password,
              // Better Auth requires a name; players don't set one in v1, so
              // derive a friendly default from the email local-part.
              name: email.split('@')[0] || email,
            });
      if (result.error) {
        setError(
          result.error.message ??
            'Something went wrong. Please check your details and try again.'
        );
      } else {
        onSuccess?.();
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="bg-mt-surface flex w-72 flex-col gap-4 rounded-2xl p-6 shadow-2xl"
    >
      <div className="text-center">
        <h1 className="text-mt-ink-soft text-xl font-bold tracking-tight">
          {heading}
        </h1>
        {subheading && (
          <p className="text-mt-ink-muted mt-1 text-sm">{subheading}</p>
        )}
      </div>

      {error && (
        <p
          role="alert"
          className="text-mt-danger rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm"
        >
          {error}
        </p>
      )}

      <label className="text-mt-ink-soft flex flex-col gap-1 text-sm font-medium">
        Email
        <input
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="text-mt-ink-soft border-mt-border focus:border-mt-primary rounded-md border px-3 py-2 focus:outline-none"
        />
      </label>

      <label className="text-mt-ink-soft flex flex-col gap-1 text-sm font-medium">
        Password
        <input
          type="password"
          autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="text-mt-ink-soft border-mt-border focus:border-mt-primary rounded-md border px-3 py-2 focus:outline-none"
        />
      </label>

      <button
        type="submit"
        disabled={busy}
        className="mt-btn bg-mt-primary text-mt-on-primary hover:bg-mt-primary-hover focus-visible:outline-mt-primary inline-flex items-center justify-center gap-x-2 rounded-md px-3.5 py-2.5 text-sm font-semibold shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-60"
      >
        {busy
          ? 'Please wait…'
          : mode === 'signin'
            ? 'Sign in'
            : 'Create account'}
      </button>

      <button
        type="button"
        onClick={() => {
          setMode((m) => (m === 'signin' ? 'signup' : 'signin'));
          setError(null);
        }}
        className="text-mt-primary text-sm hover:underline"
      >
        {mode === 'signin'
          ? 'New here? Create an account'
          : 'Already have an account? Sign in'}
      </button>
    </form>
  );
};

export default AuthForm;
